import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const LEVEL_CYCLE: string[] = ['NOT_STARTED', 'IN_PROGRESS', 'ACQUIRED', 'MASTERED']
const LEVEL_CONFIG: Record<string, { label: string; color: string }> = {
  NOT_STARTED: { label: 'Non abordé', color: 'border-white/[0.12] text-text-muted' },
  IN_PROGRESS:  { label: 'En cours',   color: 'border-warning/25 text-warning bg-warning/10' },
  ACQUIRED:     { label: 'Acquis',     color: 'border-success/25 text-success bg-success/10' },
  MASTERED:     { label: 'Maîtrisé',   color: 'border-accent/25 text-accent bg-accent/10' },
}

const TYPE_LABELS: Record<string, string> = {
  INITIALE: 'Formation initiale', RECYCLAGE: 'Recyclage', HABILITATION: 'Habilitation',
  SENSIBILISATION: 'Sensibilisation', AUTRE: 'Autre',
}
const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  PLANNED:     { label: 'Planifiée',   color: 'bg-accent/10 border-accent/25 text-accent' },
  IN_PROGRESS: { label: 'En cours',    color: 'bg-warning/10 border-warning/25 text-warning' },
  COMPLETED:   { label: 'Terminée',    color: 'bg-success/10 border-success/25 text-success' },
  CANCELLED:   { label: 'Annulée',     color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
}

interface Trainee {
  id: string
  firstName: string
  lastName: string
  email: string | null
  employer: string | null
  jobTitle: string | null
  attended: boolean
  passed: boolean | null
  createdAt: string
}

interface Material {
  id: string
  type: string
  label: string
  quantity: number
  status: string
  fourniPar: string
  notes: string | null
}

interface SessionReview {
  ratingGlobal: number
  ratingContenu: number
  ratingFormateur: number
  ratingOrga: number
  comment: string | null
}

const MATERIAL_TYPES = [
  { value: 'MANNEQUIN_SST',      label: 'Mannequin SST',        icon: '🫀' },
  { value: 'EXTINCTEUR',         label: 'Extincteur',           icon: '🧯' },
  { value: 'DEFIBRILLATEUR',     label: 'Défibrillateur',       icon: '⚡' },
  { value: 'ECRAN_PROJECTION',   label: 'Écran/Vidéoprojecteur',icon: '📽' },
  { value: 'TABLEAU_BLANC',      label: 'Tableau blanc',        icon: '📋' },
  { value: 'DOCUMENTATION',      label: 'Documentation',        icon: '📄' },
  { value: 'TENUE_EPI',          label: 'Tenue / EPI',          icon: '🦺' },
  { value: 'AUTRE',              label: 'Autre',                icon: '📦' },
]

const MATERIAL_STATUS_CFG: Record<string, { label: string; color: string }> = {
  DISPONIBLE: { label: 'Disponible', color: 'text-success bg-success/10 border-success/25' },
  A_VERIFIER: { label: 'À vérifier', color: 'text-warning bg-warning/10 border-warning/25' },
  MANQUANT:   { label: 'Manquant',   color: 'text-danger bg-danger/10 border-danger/25' },
}

const FOURNI_PAR_CFG: Record<string, string> = {
  FORMATEUR:      'Fourni par le formateur',
  ENTREPRISE:     'Fourni par l\'entreprise',
  NON_APPLICABLE: 'Non applicable',
}

interface ProgressionEntry {
  skillId: string
  traineeId: string
  level: string
  comment: string | null
}

interface ProgressionSkill {
  id: string
  label: string
  order: number
  entries: ProgressionEntry[]
}

interface Session {
  id: string
  title: string
  type: string
  status: string
  startDate: string
  endDate: string
  durationH: number
  isRemote: boolean
  address: string | null
  city: string | null
  formateurName: string | null
  objectifs: string | null
  programme: string | null
  capacity: number | null
  trainees: Trainee[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem('velink_token')}` }
}

async function downloadPdf(url: string, filename: string) {
  const res = await fetch(url, { headers: authH() })
  if (!res.ok) throw new Error('Erreur téléchargement')
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 10000)
}

export default function SessionDetailPage() {
  const params = useParams()
  const navigate = useNavigate()
  const sessionId = params.id as string

  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddTrainee, setShowAddTrainee] = useState(false)
  const [addSaving, setAddSaving] = useState(false)
  const [addError, setAddError] = useState('')
  const [dlError, setDlError] = useState('')
  const [dlLoading, setDlLoading] = useState<string | null>(null)
  const [editingTrainee, setEditingTrainee] = useState<string | null>(null)

  // Livret de progression
  const [skills, setSkills] = useState<ProgressionSkill[]>([])
  const [showLivret, setShowLivret] = useState(false)
  const [newSkillLabel, setNewSkillLabel] = useState('')
  const [addingSkill, setAddingSkill] = useState(false)
  const [livretLoaded, setLivretLoaded] = useState(false)

  // Matériaux pédagogiques
  const [materials, setMaterials] = useState<Material[]>([])
  const [showMaterials, setShowMaterials] = useState(false)
  const [materialsLoaded, setMaterialsLoaded] = useState(false)
  const [newMaterial, setNewMaterial] = useState({ type: 'MANNEQUIN_SST', label: '', quantity: 1, fourniPar: 'FORMATEUR' })
  const [addingMaterial, setAddingMaterial] = useState(false)

  // Passeport prévention
  const [passportEmail, setPassportEmail] = useState('')
  const [passportLoading, setPassportLoading] = useState(false)

  // Évaluation session
  const [review, setReview] = useState<SessionReview | null>(null)
  const [showReview, setShowReview] = useState(false)
  const [reviewLoaded, setReviewLoaded] = useState(false)
  const [reviewForm, setReviewForm] = useState({ ratingGlobal: 5, ratingContenu: 5, ratingFormateur: 5, ratingOrga: 5, comment: '' })
  const [savingReview, setSavingReview] = useState(false)

  async function load() {
    try {
      const data = await apiFetch<Session>(`/training/sessions/${sessionId}`)
      setSession(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [sessionId])

  async function handleAddTrainee(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setAddSaving(true); setAddError('')
    const fd = new FormData(e.currentTarget)
    try {
      await apiFetch(`/training/sessions/${sessionId}/trainees`, {
        method: 'POST',
        body: JSON.stringify({
          firstName: fd.get('firstName'),
          lastName: fd.get('lastName'),
          email: fd.get('email') || undefined,
          employer: fd.get('employer') || undefined,
          jobTitle: fd.get('jobTitle') || undefined,
        }),
      })
      setShowAddTrainee(false)
      ;(e.target as HTMLFormElement).reset()
      await load()
    } catch (err: unknown) {
      setAddError(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setAddSaving(false)
    }
  }

  async function toggleAttended(trainee: Trainee) {
    await apiFetch(`/training/sessions/${sessionId}/trainees/${trainee.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ attended: !trainee.attended }),
    })
    await load()
  }

  async function togglePassed(trainee: Trainee) {
    const next = trainee.passed === true ? false : trainee.passed === false ? null : true
    await apiFetch(`/training/sessions/${sessionId}/trainees/${trainee.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ passed: next }),
    })
    await load()
  }

  async function deleteTrainee(id: string) {
    if (!confirm('Supprimer ce stagiaire ?')) return
    await apiFetch(`/training/sessions/${sessionId}/trainees/${id}`, { method: 'DELETE' })
    await load()
  }

  async function updateStatus(status: string) {
    await apiFetch(`/training/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
    await load()
  }

  async function handleDownload(url: string, filename: string) {
    setDlLoading(filename); setDlError('')
    try { await downloadPdf(`${API}/api${url}`, filename) }
    catch { setDlError('Erreur lors du téléchargement') }
    finally { setDlLoading(null) }
  }

  async function deleteSession() {
    if (!confirm('Supprimer cette session et tous ses stagiaires ? Cette action est irréversible.')) return
    await apiFetch(`/training/sessions/${sessionId}`, { method: 'DELETE' })
    navigate('/sessions')
  }

  async function loadMaterials() {
    try {
      const data = await apiFetch<Material[]>(`/training/sessions/${sessionId}/materials`)
      setMaterials(data)
    } catch { /* silencieux */ }
  }

  async function toggleMaterials() {
    if (!materialsLoaded) { await loadMaterials(); setMaterialsLoaded(true) }
    setShowMaterials((v) => !v)
  }

  async function handleAddMaterial(e: React.FormEvent) {
    e.preventDefault()
    if (!newMaterial.label.trim()) return
    setAddingMaterial(true)
    try {
      await apiFetch(`/training/sessions/${sessionId}/materials`, {
        method: 'POST',
        body: JSON.stringify({ ...newMaterial }),
      })
      setNewMaterial({ type: 'MANNEQUIN_SST', label: '', quantity: 1, fourniPar: 'FORMATEUR' })
      await loadMaterials()
    } catch { /* silencieux */ }
    finally { setAddingMaterial(false) }
  }

  async function cycleMaterialStatus(mat: Material) {
    const cycle = ['DISPONIBLE', 'A_VERIFIER', 'MANQUANT']
    const next = cycle[(cycle.indexOf(mat.status) + 1) % cycle.length]
    await apiFetch(`/training/sessions/${sessionId}/materials/${mat.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next }),
    })
    await loadMaterials()
  }

  async function deleteMaterial(id: string) {
    await apiFetch(`/training/sessions/${sessionId}/materials/${id}`, { method: 'DELETE' })
    await loadMaterials()
  }

  async function handleDownloadPassport() {
    if (!passportEmail.trim()) return
    setPassportLoading(true)
    try {
      await downloadPdf(
        `${API}/api/training/passeport/pdf?email=${encodeURIComponent(passportEmail)}`,
        `passeport-${passportEmail}.pdf`,
      )
    } catch { /* silencieux */ }
    finally { setPassportLoading(false) }
  }

  async function toggleReview() {
    if (!reviewLoaded) {
      try {
        const data = await apiFetch<SessionReview | null>(`/training/sessions/${sessionId}/review`)
        if (data) { setReview(data); setReviewForm({ ratingGlobal: data.ratingGlobal, ratingContenu: data.ratingContenu, ratingFormateur: data.ratingFormateur, ratingOrga: data.ratingOrga, comment: data.comment ?? '' }) }
      } catch { /* silencieux */ }
      setReviewLoaded(true)
    }
    setShowReview((v) => !v)
  }

  async function handleSaveReview(e: React.FormEvent) {
    e.preventDefault()
    setSavingReview(true)
    try {
      const saved = await apiFetch<SessionReview>(`/training/sessions/${sessionId}/review`, {
        method: 'POST',
        body: JSON.stringify(reviewForm),
      })
      setReview(saved)
    } catch { /* silencieux */ }
    finally { setSavingReview(false) }
  }

  async function loadProgression() {
    try {
      const data = await apiFetch<ProgressionSkill[]>(`/training/sessions/${sessionId}/progression`)
      setSkills(data)
    } catch { /* silencieux */ }
  }

  async function toggleLivret() {
    if (!livretLoaded) {
      await loadProgression()
      setLivretLoaded(true)
    }
    setShowLivret((v) => !v)
  }

  async function handleAddSkill(e: React.FormEvent) {
    e.preventDefault()
    if (!newSkillLabel.trim()) return
    setAddingSkill(true)
    try {
      await apiFetch(`/training/sessions/${sessionId}/skills`, {
        method: 'POST',
        body: JSON.stringify({ label: newSkillLabel.trim() }),
      })
      setNewSkillLabel('')
      await loadProgression()
    } catch { /* silencieux */ }
    finally { setAddingSkill(false) }
  }

  async function handleDeleteSkill(skillId: string) {
    if (!confirm('Supprimer cette compétence ?')) return
    await apiFetch(`/training/sessions/${sessionId}/skills/${skillId}`, { method: 'DELETE' })
    await loadProgression()
  }

  async function cycleLevel(traineeId: string, skillId: string, currentLevel: string) {
    const idx = LEVEL_CYCLE.indexOf(currentLevel)
    const next = LEVEL_CYCLE[(idx + 1) % LEVEL_CYCLE.length]
    await apiFetch(`/training/sessions/${sessionId}/trainees/${traineeId}/progression/${skillId}`, {
      method: 'PATCH',
      body: JSON.stringify({ level: next }),
    })
    await loadProgression()
  }

  if (loading) {
    return <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="glass-card h-24 animate-pulse" />)}</div>
  }

  if (error || !session) {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-danger mb-4">{error || 'Session introuvable'}</p>
        <Link to="/sessions" className="glass-btn px-5 py-2.5 text-sm rounded-xl inline-block">← Retour</Link>
      </div>
    )
  }

  const cfg = STATUS_CONFIG[session.status]
  const attendedCount = session.trainees.filter((t) => t.attended).length
  const passedCount = session.trainees.filter((t) => t.passed === true).length

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/sessions" className="text-text-muted hover:text-text-secondary transition-colors mt-1 flex-shrink-0">←</Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2.5 py-1 rounded-full border ${cfg.color}`}>{cfg.label}</span>
            <span className="text-xs px-2.5 py-1 rounded-full border bg-white/[0.06] border-white/[0.12] text-text-secondary">
              {TYPE_LABELS[session.type]}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">{session.title}</h1>
          <p className="text-text-muted text-xs mt-1">
            {formatDate(session.startDate)} → {formatDate(session.endDate)} · {session.durationH}h
            {session.isRemote ? ' · À distance' : session.city ? ` · ${session.city}` : ''}
            {session.formateurName ? ` · ${session.formateurName}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {session.status === 'PLANNED' && (
            <button onClick={() => updateStatus('IN_PROGRESS')} className="glass-btn px-3 py-2 text-xs rounded-xl text-warning">▶ Démarrer</button>
          )}
          {session.status === 'IN_PROGRESS' && (
            <button onClick={() => updateStatus('COMPLETED')} className="glass-btn px-3 py-2 text-xs rounded-xl text-success">✓ Terminer</button>
          )}
          <button onClick={deleteSession} className="glass-btn px-3 py-2 text-xs rounded-xl text-danger border-danger/20 hover:bg-danger/10">Supprimer</button>
        </div>
      </div>

      {/* Infos session */}
      {session.objectifs && (
        <div className="glass-card p-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Objectifs pédagogiques</h2>
          <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">{session.objectifs}</p>
        </div>
      )}

      {/* PDFs */}
      <div className="glass-card p-5">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Téléchargements</h2>
        {dlError && <p className="text-danger text-xs mb-3">{dlError}</p>}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleDownload(`/training/sessions/${sessionId}/emargement`, `emargement-${session.title}.pdf`)}
            disabled={dlLoading !== null}
            className="glass-btn px-4 py-2.5 text-sm rounded-xl flex items-center gap-2"
          >
            {dlLoading === `emargement-${session.title}.pdf` ? (
              <span className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
            ) : <span>📋</span>}
            Feuille d&apos;émargement
          </button>
        </div>
      </div>

      {/* Stagiaires */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
              Stagiaires
              <span className="ml-2 font-normal text-text-muted">({session.trainees.length}{session.capacity ? `/${session.capacity}` : ''})</span>
            </h2>
            {session.trainees.length > 0 && (
              <p className="text-text-muted text-xs mt-0.5">
                {attendedCount} présent{attendedCount > 1 ? 's' : ''} · {passedCount} validé{passedCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowAddTrainee((v) => !v)}
            className="glass-btn-accent px-3 py-2 text-xs rounded-xl"
          >
            {showAddTrainee ? 'Annuler' : '+ Ajouter'}
          </button>
        </div>

        {showAddTrainee && (
          <form onSubmit={handleAddTrainee} className="mb-5 p-4 rounded-xl bg-white/[0.04] border border-white/[0.08] space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="glass-label">Prénom *</label>
                <input name="firstName" className="glass-input" required />
              </div>
              <div>
                <label className="glass-label">Nom *</label>
                <input name="lastName" className="glass-input" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="glass-label">Email</label>
                <input name="email" type="email" className="glass-input" />
              </div>
              <div>
                <label className="glass-label">Employeur</label>
                <input name="employer" className="glass-input" placeholder="Nom de l'entreprise" />
              </div>
            </div>
            <div>
              <label className="glass-label">Fonction</label>
              <input name="jobTitle" className="glass-input" placeholder="Ex : Agent de sécurité" />
            </div>
            {addError && <p className="text-danger text-xs">{addError}</p>}
            <button type="submit" disabled={addSaving} className="glass-btn-accent px-5 py-2 text-sm rounded-xl">
              {addSaving ? 'Ajout…' : 'Ajouter le stagiaire'}
            </button>
          </form>
        )}

        {session.trainees.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-3xl mb-3 opacity-40">👥</p>
            <p className="text-text-secondary text-sm">Aucun stagiaire inscrit</p>
          </div>
        ) : (
          <div className="space-y-2">
            {session.trainees.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.07] group">
                <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent text-xs font-bold">{t.firstName[0]}{t.lastName[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary text-sm font-medium">{t.lastName.toUpperCase()} {t.firstName}</p>
                  <p className="text-text-muted text-xs">
                    {[t.jobTitle, t.employer].filter(Boolean).join(' · ') || t.email || '—'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Présence */}
                  <button
                    onClick={() => toggleAttended(t)}
                    title="Présence"
                    className={`text-xs px-2 py-1 rounded-lg border transition-all ${
                      t.attended
                        ? 'bg-success/10 border-success/25 text-success'
                        : 'border-white/[0.12] text-text-muted hover:border-white/[0.25]'
                    }`}
                  >
                    {t.attended ? '✓ Présent' : '○ Absent'}
                  </button>
                  {/* Résultat */}
                  <button
                    onClick={() => togglePassed(t)}
                    title="Résultat"
                    className={`text-xs px-2 py-1 rounded-lg border transition-all ${
                      t.passed === true
                        ? 'bg-success/10 border-success/25 text-success'
                        : t.passed === false
                        ? 'bg-danger/10 border-danger/25 text-danger'
                        : 'border-white/[0.12] text-text-muted hover:border-white/[0.25]'
                    }`}
                  >
                    {t.passed === true ? '✓ Validé' : t.passed === false ? '✗ Non validé' : '— Résultat'}
                  </button>
                  {/* Attestation */}
                  <button
                    onClick={() => handleDownload(
                      `/training/sessions/${sessionId}/trainees/${t.id}/attestation`,
                      `attestation-${t.lastName}-${t.firstName}.pdf`,
                    )}
                    disabled={dlLoading !== null}
                    title="Télécharger l'attestation"
                    className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-secondary hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {dlLoading === `attestation-${t.lastName}-${t.firstName}.pdf` ? (
                      <span className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin block" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/>
                      </svg>
                    )}
                  </button>
                  {/* Déclaration passeport prévention */}
                  <button
                    onClick={() => handleDownload(
                      `/training/sessions/${sessionId}/trainees/${t.id}/declaration-passeport`,
                      `declaration-passeport-${t.lastName}-${t.firstName}.pdf`,
                    )}
                    disabled={dlLoading !== null}
                    title="Déclaration passeport prévention"
                    className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-secondary hover:text-[#F59E0B] transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {dlLoading === `declaration-passeport-${t.lastName}-${t.firstName}.pdf` ? (
                      <span className="w-4 h-4 border-2 border-[#F59E0B]/30 border-t-[#F59E0B] rounded-full animate-spin block" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    )}
                  </button>
                  {/* Supprimer */}
                  <button
                    onClick={() => deleteTrainee(t.id)}
                    title="Supprimer"
                    className="p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Matériel pédagogique */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Matériel pédagogique</h2>
          <button onClick={toggleMaterials} className="glass-btn px-3 py-2 text-xs rounded-xl">
            {showMaterials ? 'Masquer' : 'Afficher'}
          </button>
        </div>
        {showMaterials && (
          <div className="mt-4 space-y-4">
            <form onSubmit={handleAddMaterial} className="grid grid-cols-12 gap-2">
              <select value={newMaterial.type} onChange={(e) => setNewMaterial((v) => ({ ...v, type: e.target.value, label: MATERIAL_TYPES.find(t => t.value === e.target.value)?.label ?? '' }))} className="glass-input col-span-3 text-sm">
                {MATERIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
              </select>
              <input value={newMaterial.label} onChange={(e) => setNewMaterial((v) => ({ ...v, label: e.target.value }))} className="glass-input col-span-3 text-sm" placeholder="Libellé" required />
              <input type="number" min={1} value={newMaterial.quantity} onChange={(e) => setNewMaterial((v) => ({ ...v, quantity: Number(e.target.value) }))} className="glass-input col-span-1 text-sm" placeholder="Qté" />
              <select value={newMaterial.fourniPar} onChange={(e) => setNewMaterial((v) => ({ ...v, fourniPar: e.target.value }))} className="glass-input col-span-3 text-sm">
                <option value="FORMATEUR">Formateur</option>
                <option value="ENTREPRISE">Entreprise</option>
                <option value="NON_APPLICABLE">N/A</option>
              </select>
              <button type="submit" disabled={addingMaterial || !newMaterial.label.trim()} className="glass-btn-accent col-span-2 py-2 text-xs rounded-xl">
                {addingMaterial ? '…' : '+ Ajouter'}
              </button>
            </form>
            {materials.length === 0 ? (
              <p className="text-text-muted text-xs italic">Aucun matériel défini.</p>
            ) : (
              <div className="space-y-2">
                {materials.map((mat) => {
                  const typeCfg = MATERIAL_TYPES.find((t) => t.value === mat.type)
                  const statusCfg = MATERIAL_STATUS_CFG[mat.status]
                  return (
                    <div key={mat.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.07] group">
                      <span className="text-lg">{typeCfg?.icon ?? '📦'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-text-secondary text-sm font-medium">{mat.label} <span className="text-text-muted font-normal">×{mat.quantity}</span></p>
                        <p className="text-text-muted text-xs">{FOURNI_PAR_CFG[mat.fourniPar]}</p>
                      </div>
                      <button onClick={() => cycleMaterialStatus(mat)} className={`text-[10px] px-2 py-1 rounded-full border cursor-pointer transition-all ${statusCfg.color}`}>
                        {statusCfg.label}
                      </button>
                      <button onClick={() => deleteMaterial(mat.id)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-all">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Passeport prévention */}
      <div className="glass-card p-5">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Passeport prévention</h2>
        <p className="text-text-muted text-xs mb-3">Générez le passeport récapitulatif de toutes les formations d&apos;un stagiaire (par email).</p>
        <div className="flex gap-2">
          <input
            value={passportEmail}
            onChange={(e) => setPassportEmail(e.target.value)}
            className="glass-input flex-1 text-sm"
            placeholder="email@stagiaire.fr"
            type="email"
          />
          <button
            onClick={handleDownloadPassport}
            disabled={!passportEmail.trim() || passportLoading}
            className="glass-btn px-4 py-2 text-sm rounded-xl whitespace-nowrap flex items-center gap-2"
          >
            {passportLoading ? <span className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" /> : '📋'}
            Générer PDF
          </button>
        </div>
      </div>

      {/* Évaluation de la session */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Évaluation de la session</h2>
            {review && <div className="flex gap-1 mt-1">{'★'.repeat(review.ratingGlobal).split('').map((s, i) => <span key={i} className="text-warning text-sm">{s}</span>)}</div>}
          </div>
          <button onClick={toggleReview} className="glass-btn px-3 py-2 text-xs rounded-xl">
            {showReview ? 'Masquer' : review ? 'Modifier' : 'Évaluer'}
          </button>
        </div>
        {showReview && (
          <form onSubmit={handleSaveReview} className="mt-4 space-y-4">
            {(['ratingGlobal', 'ratingContenu', 'ratingFormateur', 'ratingOrga'] as const).map((field) => {
              const labels: Record<string, string> = { ratingGlobal: 'Note globale', ratingContenu: 'Qualité du contenu', ratingFormateur: 'Qualité du formateur', ratingOrga: 'Organisation' }
              return (
                <div key={field}>
                  <label className="glass-label mb-1">{labels[field]}</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n} type="button"
                        onClick={() => setReviewForm((v) => ({ ...v, [field]: n }))}
                        className={`w-10 h-10 rounded-xl border text-lg transition-all ${reviewForm[field] >= n ? 'text-warning border-warning/40 bg-warning/10' : 'text-text-muted border-white/[0.10] hover:border-white/[0.20]'}`}
                      >★</button>
                    ))}
                    <span className="ml-2 self-center text-text-secondary text-sm font-medium">{reviewForm[field]}/5</span>
                  </div>
                </div>
              )
            })}
            <div>
              <label className="glass-label">Commentaire</label>
              <textarea value={reviewForm.comment} onChange={(e) => setReviewForm((v) => ({ ...v, comment: e.target.value }))} className="glass-input min-h-[70px]" placeholder="Points forts, axes d'amélioration…" />
            </div>
            <button type="submit" disabled={savingReview} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
              {savingReview ? 'Enregistrement…' : 'Enregistrer l\'évaluation'}
            </button>
          </form>
        )}
      </div>

      {/* Livret de progression */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">Livret de progression</h2>
          <button
            onClick={toggleLivret}
            className="glass-btn px-3 py-2 text-xs rounded-xl"
          >
            {showLivret ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        {showLivret && (
          <div className="mt-4 space-y-5">
            {/* Gestion des compétences */}
            <div>
              <h3 className="text-xs font-semibold text-text-secondary mb-3">Compétences évaluées</h3>
              <form onSubmit={handleAddSkill} className="flex gap-2 mb-3">
                <input
                  value={newSkillLabel}
                  onChange={(e) => setNewSkillLabel(e.target.value)}
                  className="glass-input flex-1 text-sm"
                  placeholder="Ex : Maîtrise des extincteurs…"
                />
                <button type="submit" disabled={addingSkill || !newSkillLabel.trim()} className="glass-btn-accent px-4 py-2 text-xs rounded-xl whitespace-nowrap">
                  {addingSkill ? '…' : '+ Ajouter'}
                </button>
              </form>
              {skills.length === 0 ? (
                <p className="text-text-muted text-xs italic">Aucune compétence. Ajoutez-en pour construire la grille.</p>
              ) : (
                <div className="space-y-1.5">
                  {skills.map((sk) => (
                    <div key={sk.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.07] group">
                      <span className="text-text-secondary text-sm flex-1">{sk.label}</span>
                      <button
                        onClick={() => handleDeleteSkill(sk.id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-all"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Grille compétences × stagiaires */}
            {skills.length > 0 && session.trainees.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-text-secondary mb-3">Matrice de progression</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left text-text-muted font-medium px-3 py-2 bg-white/[0.04] rounded-tl-lg min-w-[180px]">Compétence</th>
                        {session.trainees.map((t) => (
                          <th key={t.id} className="text-center text-text-secondary font-medium px-2 py-2 bg-white/[0.04] min-w-[100px]">
                            <div className="truncate max-w-[90px] mx-auto" title={`${t.firstName} ${t.lastName}`}>
                              {t.firstName}<br /><span className="font-bold">{t.lastName.toUpperCase()}</span>
                            </div>
                          </th>
                        ))}
                        <th className="bg-white/[0.04] rounded-tr-lg px-2 py-2 text-text-muted font-medium">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skills.map((sk) => (
                        <tr key={sk.id} className="border-t border-white/[0.06]">
                          <td className="px-3 py-2 text-text-secondary">{sk.label}</td>
                          {session.trainees.map((t) => {
                            const entry = sk.entries.find((e) => e.traineeId === t.id)
                            const level = entry?.level ?? 'NOT_STARTED'
                            const cfg = LEVEL_CONFIG[level]
                            return (
                              <td key={t.id} className="px-2 py-2 text-center">
                                <button
                                  onClick={() => cycleLevel(t.id, sk.id, level)}
                                  className={`text-[10px] px-2 py-1 rounded-full border transition-all cursor-pointer hover:opacity-80 ${cfg.color}`}
                                  title="Cliquer pour changer le niveau"
                                >
                                  {cfg.label}
                                </button>
                              </td>
                            )
                          })}
                          <td className="px-2 py-2"></td>
                        </tr>
                      ))}
                      {/* Ligne PDF par stagiaire */}
                      <tr className="border-t border-white/[0.10]">
                        <td className="px-3 py-2 text-text-muted font-medium">Livret PDF</td>
                        {session.trainees.map((t) => (
                          <td key={t.id} className="px-2 py-2 text-center">
                            <button
                              onClick={() => handleDownload(
                                `/training/sessions/${sessionId}/trainees/${t.id}/livret`,
                                `livret-${t.lastName}-${t.firstName}.pdf`,
                              )}
                              disabled={dlLoading !== null}
                              className="glass-btn px-2 py-1 text-[10px] rounded-lg"
                              title={`Télécharger le livret de ${t.firstName}`}
                            >
                              {dlLoading === `livret-${t.lastName}-${t.firstName}.pdf` ? (
                                <span className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin block mx-auto" />
                              ) : '⬇ PDF'}
                            </button>
                          </td>
                        ))}
                        <td className="px-2 py-2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-text-muted text-[10px] mt-2">Cliquez sur un niveau pour le faire évoluer : Non abordé → En cours → Acquis → Maîtrisé</p>
              </div>
            )}

            {skills.length > 0 && session.trainees.length === 0 && (
              <p className="text-text-muted text-xs italic">Ajoutez des stagiaires pour remplir la grille.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
